import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { money, date, useLedger, useTenantCatalog, useSubscriptions } from '../hooks';
import type { Invoice, Page } from '../types';

/**
 * Сводка тенанта: сколько начислено и получено, кто должен, что настроено. Цифры берутся из
 * леджера, а не пересчитываются по экранам, — иначе сводка разошлась бы с бухгалтерией.
 */
export function DashboardPage() {
  const { tenantId = '' } = useParams();
  const ledger = useLedger(tenantId);
  const catalog = useTenantCatalog(tenantId);
  const subscriptions = useSubscriptions(tenantId);
  const invoices = useQuery({
    queryKey: ['invoices', tenantId, 'recent'],
    queryFn: () => api.get<Page<Invoice>>(`/tenants/${tenantId}/invoices?size=8&sort=issuedAt,desc`),
  });

  const accounts = ledger.data ?? [];
  const balance = (code: string) => accounts.find((a) => a.code === code)?.balanceKopecks ?? 0;
  const revenue = balance('REVENUE');
  const commission = balance('COMMISSION_REVENUE');
  const cash = balance('CASH');
  const receivable = accounts
    .filter((a) => a.ownerType === 'CUSTOMER')
    .reduce((sum, a) => sum + a.balanceKopecks, 0);
  const payable = accounts
    .filter((a) => a.ownerType === 'PAYEE')
    .reduce((sum, a) => sum + a.balanceKopecks, 0);

  const activeSubscriptions = (subscriptions.data?.content ?? []).filter((s) => s.status === 'ACTIVE');

  return (
    <>
      <div className="page-head">
        <h1>Сводка</h1>
      </div>

      <div className="metrics">
        <Metric title="Начислено" value={revenue} hint="выручка по выставленным счетам" />
        <Metric title="Получено" value={cash} hint="деньги на счетах тенанта" />
        <Metric title="Дебиторка" value={receivable} hint="выставлено и не оплачено" accent={receivable > 0} />
        {commission > 0 && <Metric title="Комиссия" value={commission} hint="с разовых продаж" />}
        {payable > 0 && <Metric title="К выплате" value={payable} hint="долг исполнителям" />}
      </div>

      <div className="cards-row">
        <section className="card">
          <div className="card-head">
            <h2>Настройка</h2>
            <Link className="card-link" to={`/t/${tenantId}/plans`}>
              Тарифы
            </Link>
          </div>
          <ul className="facts">
            <li>
              <span>Абоненты</span>
              <b>{catalog.customers.length}</b>
            </li>
            <li>
              <span>Счётчики</span>
              <b>{catalog.meters.length}</b>
            </li>
            <li>
              <span>Тарифы</span>
              <b>{catalog.plans.length}</b>
            </li>
            <li>
              <span>Действующие договоры</span>
              <b>{activeSubscriptions.length}</b>
            </li>
          </ul>
          {catalog.meters.length === 0 && (
            <div className="form-hint">
              Начните со <Link to={`/t/${tenantId}/meters`}>счётчика</Link>: он описывает, что вы
              измеряете. Затем соберите <Link to={`/t/${tenantId}/plans`}>тариф</Link> и заключите
              договор с абонентом.
            </div>
          )}
        </section>

        <section className="card grow">
          <div className="card-head">
            <h2>Последние счета</h2>
            <Link className="card-link" to={`/t/${tenantId}/invoices`}>
              Все счета
            </Link>
          </div>
          {invoices.isLoading ? (
            <div className="state">Загрузка…</div>
          ) : (invoices.data?.content.length ?? 0) === 0 ? (
            <div className="state">Счетов пока нет</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Счёт</th>
                  <th>Абонент</th>
                  <th className="num">Сумма</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {invoices.data!.content.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <Link to={`/t/${tenantId}/invoices/${invoice.id}`}>{invoice.number}</Link>
                      <div className="muted small">{date(invoice.issuedAt)}</div>
                    </td>
                    <td>{catalog.customerById.get(invoice.customerId)?.name ?? '—'}</td>
                    <td className="num">{money(invoice.totalKopecks)}</td>
                    <td>
                      <InvoiceStatus status={invoice.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

function Metric({ title, value, hint, accent }: {
  title: string;
  value: number;
  hint: string;
  accent?: boolean;
}) {
  return (
    <div className={`metric${accent ? ' accent' : ''}`}>
      <div className="metric-title">{title}</div>
      <div className="metric-value">{money(value)} ₽</div>
      <div className="muted small">{hint}</div>
    </div>
  );
}

/** Статус счёта цветом: выставлен — нейтрально, оплачен — зелёный, аннулирован — приглушённый. */
export function InvoiceStatus({ status }: { status: 'ISSUED' | 'PAID' | 'VOID' }) {
  const map = {
    ISSUED: { label: 'выставлен', className: 'badge badge-blue' },
    PAID: { label: 'оплачен', className: 'badge badge-green' },
    VOID: { label: 'аннулирован', className: 'badge badge-gray' },
  } as const;
  return <span className={map[status].className}>{map[status].label}</span>;
}
